import React, { useState } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { AntSwitch } from '../../../components/antSwitch';
import { useNavigate } from 'react-router-dom';
import DeleteModal from './deleteModal';
import StatusModal from './statusModal';
import SignupModal from './signupModal';
import LoginMethodModal from './loginMethodModal';
import { tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { personaLabel } from '../../../constant/personaLabel';

const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

const PersonaIconCell = ({ iconUrl }) => {
  const [failed, setFailed] = useState(false);
  const showImage = iconUrl && !failed;

  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        border: '1px solid #E3E8F2',
        backgroundColor: '#F7F9FD',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={`${iconUrl}?${sasToken}`}
          alt="Persona icon"
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon icon="mdi:image-outline" width="20" height="20" color="#9AA3B5" />
      )}
    </Box>
  );
};

const formatPersonaTypeName = personaLabel;

const textCellSx = {
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: '#1C1B1F',
};

export default function UserTypeCoulmn(props) {
  const navigate = useNavigate();
  const { setAlert, setOpenAlert, setResponseMsg, setLoading, getUserTypeData, setActionError } = props;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openSignupModal, setOpenSignupModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedShowOnSignUp, setSelectedShowOnSignUp] = useState(null);
  const [openLoginMethodModal, setOpenLoginMethodModal] = useState(false);
  const [selectedLoginMethod, setSelectedLoginMethod] = useState(null);
  const ALERT_DURATION = 2000;

  const alertClose = () => {
    setAlert('');
  };

  const handleOpenDeleteModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenDeleteModal(true);
  };

  const handleOpenStatusModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenStatusModal(true);
  };

  const handleOpenSignupModal = (id, showOnSignUp) => {
    setSelectedId(id);
    setSelectedShowOnSignUp(showOnSignUp);
    setOpenSignupModal(true);
  };

  const handleOpenLoginMethodModal = (id, loginMethod) => {
    setSelectedId(id);
    setSelectedLoginMethod(loginMethod);
    setOpenLoginMethodModal(true);
  };

  const userTypeColumn = [
    {
      field: 'iconUrl',
      headerName: 'Icon',
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => <PersonaIconCell iconUrl={params.value} />,
    },
    {
      field: 'name',
      headerName: 'User Persona',
      flex: 1.1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ ...textCellSx }}>
          {formatPersonaTypeName(params.value) || '-'}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.6,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const htmlString = params.value || '';
        const plainText = htmlString.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        return (
          <Typography sx={{ ...textCellSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {plainText || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'countryNames',
      headerName: 'Country',
      flex: 0.9,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ ...textCellSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {Array.isArray(params.value) && params.value.length > 0 ? params.value.join(', ') : '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const isActive = params.row.status === 'ACTIVE';
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <AntSwitch checked={isActive} onChange={() => handleOpenStatusModal(params.row.id, params.row.status)} inputProps={{ 'aria-label': 'controlled' }} />
              <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: isActive ? '#4F8506' : '#625B71' }}>
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
            <Modal open={openStatusModal} onClose={() => setOpenStatusModal(false)} BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
              <div>
                <StatusModal onClose={() => setOpenStatusModal(false)} getUserTypeData={getUserTypeData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} setActionError={setActionError} />
              </div>
            </Modal>
          </>
        );
      },
    },
    {
      field: 'showOnSignUp',
      headerName: 'Sign Up',
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const showOnSignUp = params.row.showOnSignUp || false;
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <AntSwitch checked={showOnSignUp} onChange={() => handleOpenSignupModal(params.row.id, showOnSignUp)} inputProps={{ 'aria-label': 'controlled' }} />
              <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: showOnSignUp ? '#4F8506' : '#625B71' }}>
                {showOnSignUp ? 'Yes' : 'No'}
              </Typography>
            </Box>
            <Modal open={openSignupModal} onClose={() => setOpenSignupModal(false)} BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
              <div>
                <SignupModal onClose={() => setOpenSignupModal(false)} getUserTypeData={getUserTypeData} selectedId={selectedId} showOnSignUp={selectedShowOnSignUp} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} setActionError={setActionError} />
              </div>
            </Modal>
          </>
        );
      },
    },
    {
      // PIN login (feature/pin-login): on = this persona signs in with a PIN, off = SMS code.
      field: 'loginMethod',
      headerName: 'Login',
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const loginMethod = params.row.loginMethod === 'PIN' ? 'PIN' : 'OTP';
        const usesPin = loginMethod === 'PIN';
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
              <AntSwitch checked={usesPin} onChange={() => handleOpenLoginMethodModal(params.row.id, loginMethod)} inputProps={{ 'aria-label': `Login method for ${formatPersonaTypeName(params.row.name)}` }} />
              <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: usesPin ? '#4F8506' : '#625B71' }}>
                {usesPin ? 'PIN' : 'OTP'}
              </Typography>
            </Box>
            <Modal open={openLoginMethodModal} onClose={() => setOpenLoginMethodModal(false)} BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
              <div>
                <LoginMethodModal onClose={() => setOpenLoginMethodModal(false)} getUserTypeData={getUserTypeData} selectedId={selectedId} loginMethod={selectedLoginMethod} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} setActionError={setActionError} />
              </div>
            </Modal>
          </>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1.1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
            <Button
              onClick={() => navigate(`/userTypeView/${params.row.id}?type=edit`)}
              startIcon={<span className="material-symbols-rounded">visibility</span>}
              sx={tableViewButtonSx}
            >
              View
            </Button>
            <Button
              onClick={() => handleOpenDeleteModal(params.row.id, params.row.status)}
              startIcon={<span className="material-symbols-rounded">delete</span>}
              sx={{ ...tableDeleteButtonSx, minWidth: 96 }}
            >
              Delete
            </Button>
          </Box>
          <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}>
            <div>
              <DeleteModal onClose={() => setOpenDeleteModal(false)} getUserTypeData={getUserTypeData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} setActionError={setActionError} />
            </div>
          </Modal>
        </>
      ),
    },
  ];

  return userTypeColumn;
}
